import React from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2
} from '@itandi/itandi-bb-ui';
import { useForm } from 'react-hook-form';
import type { Deal } from '~/models/Deal';
import { toDate } from '~/utils/DateUtil';
import { isSuccess } from '~/utils/api/api_handler';
import { getSelfDealVendor } from '~/models/DealVendorCompany';
import { useSessions } from '~/hooks/vendors/useSessions';
import { update } from '~/utils/api/vendors/DealVisitDate';
import type { VisitScheduleInputVendorParam } from '~/models/DealVisitDate';
import { ScheduleForm } from './ScheduleForm';

type Props = {
  deal: Deal;
  isOpen: boolean;
  handleClose: () => void;
  handleMutate: () => Promise<void>;
};

/**
 * 立会日程・現地調査日程登録モーダル
 *
 * @for vendor
 */

export const ScheduleInputModal: React.FC<Props> = ({
  deal,
  isOpen,
  handleClose,
  handleMutate
}: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const session = useSessions().index().data!;
  const selfVendorUserId = React.useMemo(() => {
    const selfVendor = getSelfDealVendor(deal, session.vendorCompany.id);
    if (selfVendor === null) return null;
    return selfVendor.user?.id ?? null;
  }, [deal, session]);
  const methods = useForm<VisitScheduleInputVendorParam>({
    defaultValues: {
      inspectionDate: toDate(deal.inspectionDate),
      surveyDate: toDate(deal.surveyDate),
      vendorUserId: selfVendorUserId
    }
  });
  const { watch, formState, handleSubmit } = methods;
  const inspectionDate = watch('inspectionDate');
  const surveyDate = watch('surveyDate');
  const vendorUserId = watch('vendorUserId');
  const onClickSubmit = React.useCallback(() => {
    handleSubmit(async () => {
      const response = await update(
        deal.id,
        inspectionDate,
        surveyDate,
        vendorUserId
      );
      if (isSuccess(response)) {
        await handleMutate();
        toast.success('追加しました');
        handleClose();
      } else {
        toast.error('エラーが発生しました');
      }
    })();
  }, [
    deal,
    handleClose,
    handleMutate,
    handleSubmit,
    inspectionDate,
    surveyDate,
    vendorUserId
  ]);
  return (
    <Modalv2 isOpen={isOpen} onClose={handleClose}>
      <ModalHeader
        title={`${
          deal.inspectionBy === 'vendor'
            ? '立会日程・現地調査日程'
            : '現地調査日程'
        }登録`}
      />
      <ModalBody>
        <ScheduleForm deal={deal} methods={methods} session={session} />
      </ModalBody>
      <ModalFooter
        leftArea={
          <Button
            onClick={(): void => {
              handleClose();
            }}
            variant="secondary"
          >
            キャンセル
          </Button>
        }
        rightArea={
          <Button
            disabled={formState.isSubmitting}
            onClick={onClickSubmit}
            variant="primary"
          >
            確定
          </Button>
        }
      />
    </Modalv2>
  );
};
